import { s as sanitizeFramePath, t as computeErrorFingerprint } from "./errors-CPWzJd3r.js";
import { SpanStatusCode } from "@opentelemetry/api";
import { Resource } from "@opentelemetry/resources";

//#region src/telemetry/attributes.ts
/**
* Shared OTel resource attributes that apply to all Vortex processes.
* Both main and renderer spread this into their Resource at startup.
* Add cross-process attributes here as needed.
*/
const SHARED_TELEMETRY_ATTRIBUTES = { "deployment.environment": "test" };

//#endregion
//#region src/telemetry/spans.ts
/**
* Record an error on a span: compute fingerprint, record the exception,
* and set ERROR status. Optionally attach extra attributes and
* ambient context entries (prefixed with `context.`).
*
* The error's message and stack are passed through `sanitizeFramePath`
* before being attached, so OS usernames and absolute install paths do
* not reach the telemetry backend (GDPR Art. 5(1)(c) data minimisation).
* The caller's Error object is not mutated.
*/
const recordErrorOnSpan = (span, error, appVersion, context, attributes) => {
	const sanitizedMessage = sanitizeFramePath(error.message);
	const sanitizedStack = error.stack !== void 0 ? sanitizeFramePath(error.stack) : void 0;
	const sanitized = new Error(sanitizedMessage);
	sanitized.name = error.name;
	sanitized.stack = sanitizedStack;
	if (attributes !== void 0) for (const [key, value] of Object.entries(attributes)) span.setAttribute(key, value);
	if (context !== void 0) for (const [key, value] of Object.entries(context)) span.setAttribute(`context.${key}`, value);
	const fingerprint = computeErrorFingerprint(sanitizedStack, appVersion);
	if (fingerprint !== void 0) span.setAttribute("error.fingerprint", fingerprint);
	span.recordException(sanitized);
	span.setStatus({
		code: SpanStatusCode.ERROR,
		message: sanitizedMessage
	});
};

//#endregion
//#region src/telemetry/types.ts
/** Convert a ReadableSpan into plain serializable data for IPC. */
const serializeSpan = (span) => {
	const ctx = span.spanContext();
	return {
		name: span.name,
		kind: span.kind,
		traceId: ctx.traceId,
		spanId: ctx.spanId,
		traceFlags: ctx.traceFlags,
		parentSpanId: span.parentSpanId,
		startTime: span.startTime,
		endTime: span.endTime,
		status: {
			code: span.status.code,
			message: span.status.message
		},
		attributes: { ...span.attributes },
		links: span.links.map((link) => ({
			context: {
				traceId: link.context.traceId,
				spanId: link.context.spanId,
				traceFlags: link.context.traceFlags
			},
			attributes: link.attributes ? { ...link.attributes } : void 0
		})),
		events: span.events.map((event) => ({
			name: event.name,
			time: event.time,
			attributes: event.attributes ? { ...event.attributes } : void 0
		})),
		duration: span.duration,
		resource: { ...span.resource.attributes },
		instrumentationLibrary: {
			name: span.instrumentationLibrary.name,
			version: span.instrumentationLibrary.version,
			schemaUrl: span.instrumentationLibrary.schemaUrl
		},
		droppedAttributesCount: span.droppedAttributesCount,
		droppedEventsCount: span.droppedEventsCount,
		droppedLinksCount: span.droppedLinksCount
	};
};
/** Reconstruct a ReadableSpan-compatible object from serialized IPC data. */
const deserializeSpan = (data) => {
	return {
		name: data.name,
		kind: data.kind,
		spanContext: () => ({
			traceId: data.traceId,
			spanId: data.spanId,
			traceFlags: data.traceFlags
		}),
		parentSpanId: data.parentSpanId,
		startTime: data.startTime,
		endTime: data.endTime,
		status: data.status,
		attributes: data.attributes,
		links: data.links.map((link) => ({
			context: {
				traceId: link.context.traceId,
				spanId: link.context.spanId,
				traceFlags: link.context.traceFlags
			},
			attributes: link.attributes
		})),
		events: data.events.map((event) => ({
			name: event.name,
			time: event.time,
			attributes: event.attributes
		})),
		duration: data.duration,
		ended: true,
		resource: new Resource(data.resource),
		instrumentationLibrary: data.instrumentationLibrary,
		droppedAttributesCount: data.droppedAttributesCount,
		droppedEventsCount: data.droppedEventsCount,
		droppedLinksCount: data.droppedLinksCount
	};
};

//#endregion
export { SHARED_TELEMETRY_ATTRIBUTES, deserializeSpan, recordErrorOnSpan, serializeSpan };
//# sourceMappingURL=telemetry.js.map